import React from 'react';

import { UnsavedChangesConfirmationModal } from './UnsavedChangesConfirmationModal';

interface UnsavedChangesConfirmationModalState {
  open: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
}

/**
 * @param predicate - Whether to show the confirmation modal
 * @param params.onConfirm - Called when user confirms discard, then dismisses the modal
 * @param params.onCancel - Called when user cancels, then dismisses the modal
 */
export type ConfirmWhen = (
  predicate: boolean,
  params: {
    onConfirm: () => void;
    onCancel?: () => void;
  }
) => void;

const UnsavedChangesConfirmationModalContext = React.createContext<ConfirmWhen | null>(null);

export const useConfirmUnsavedChanges = (): { confirmWhen: ConfirmWhen } => {
  const confirmWhen = React.useContext<ConfirmWhen | null>(UnsavedChangesConfirmationModalContext);
  if (!confirmWhen) throw new Error('useConfirmUnsavedChanges must be used within a provider');
  return { confirmWhen };
};

export const UnsavedChangesConfirmationModalProvider: React.FC<{
  children?: React.ReactNode | undefined;
}> = ({ children }): JSX.Element => {
  const [state, setState] = React.useState<UnsavedChangesConfirmationModalState>({ open: false });
  const confirmWhen: ConfirmWhen = React.useCallback((predicate, params) => {
    if (predicate) {
      setState({ ...params, open: true });
    } else {
      params.onConfirm();
    }
  }, []);
  const onDiscard = (): void => {
    state.onConfirm?.();
    setState({ open: false });
  };
  const onCancel = (): void => {
    state.onCancel?.();
    setState({ open: false });
  };
  return (
    <UnsavedChangesConfirmationModalContext.Provider value={confirmWhen}>
      {children}
      <UnsavedChangesConfirmationModal
        open={state.open}
        onCancel={onCancel}
        onDiscard={onDiscard}
      />
    </UnsavedChangesConfirmationModalContext.Provider>
  );
};
